import type { EnvVarType } from '@stratumn/studio-shared';
import { envVarValueSchema } from '@stratumn/studio-shared';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  EnvVarCellCenter,
  EnvVarCellInput,
  EnvVarCellText,
  EnvVarTable
} from '@/components/env-var/EnvVarTable';
import { EnvVarValueCell } from '@/components/env-var/EnvVarValueCell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import useStores from '@/hooks/useStore';
import { extractErrorMessage } from '@/services/errors/errors';

type EnvOverridesSectionProps = {
  processId: string;
  workspaceId: string;
};

type DraftOverride = {
  envVarId: string;
  name: string;
  type: EnvVarType;
  value: string;
  enabled: boolean;
};

export const EnvOverridesSection = observer(
  ({ processId, workspaceId }: EnvOverridesSectionProps) => {
    const { envVarStore } = useStores();
    const [drafts, setDrafts] = useState<DraftOverride[]>([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      const load = async () => {
        try {
          await envVarStore.fetchEnvVars(processId);
          await envVarStore.fetchOverrides(processId, workspaceId);
        } catch {
          // errors already reported via newError → Sentry; suppress unhandled rejection
        }
      };
      load();
    }, [envVarStore, processId, workspaceId]);

    const sharedVars = useMemo(
      () => envVarStore.envVars.filter(v => v.shared),
      [envVarStore.envVars]
    );

    const overrides = envVarStore.getOverridesForWorkspace(workspaceId);

    useEffect(() => {
      const newDrafts: DraftOverride[] = overrides
        .map(o => {
          const processVar = sharedVars.find(v => v.id === o.envVarId);
          if (!processVar) {
            return null;
          }
          return {
            envVarId: o.envVarId,
            name: processVar.name,
            type: processVar.type,
            value: o.value,
            enabled: o.enabled
          };
        })
        .filter((d): d is DraftOverride => d !== null);
      setDrafts(newDrafts);
      setDirty(false);
    }, [overrides, sharedVars]);

    const availableVarsToAdd = useMemo(() => {
      const overriddenIds = new Set(drafts.map(d => d.envVarId));
      return sharedVars.filter(v => !overriddenIds.has(v.id));
    }, [sharedVars, drafts]);

    const handleAddOverride = useCallback(
      (envVarId: string) => {
        const processVar = sharedVars.find(v => v.id === envVarId);
        if (!processVar) {
          return;
        }
        setDrafts(prev => [
          ...prev,
          {
            envVarId: processVar.id,
            name: processVar.name,
            type: processVar.type,
            value: processVar.value,
            enabled: true
          }
        ]);
        setDirty(true);
      },
      [sharedVars]
    );

    const handleRemoveOverride = useCallback((index: number) => {
      setDrafts(prev => prev.filter((_, i) => i !== index));
      setDirty(true);
    }, []);

    const updateDraft = useCallback(
      (index: number, patch: Partial<DraftOverride>) => {
        setDrafts(prev => {
          const next = [...prev];
          next[index] = { ...next[index], ...patch };
          return next;
        });
        setDirty(true);
      },
      []
    );

    const handleSave = useCallback(async () => {
      for (const draft of drafts) {
        const result = envVarValueSchema(draft.type).safeParse(draft.value);
        if (!result.success) {
          const message = result.error.issues[0]?.message ?? 'Invalid value';
          toast.error(`"${draft.name}": ${message}`);
          return;
        }
      }
      setSaving(true);
      try {
        await envVarStore.bulkUpsertOverrides(processId, workspaceId, {
          overrides: drafts.map(d => ({
            envVarId: d.envVarId,
            value: d.value,
            enabled: d.enabled
          }))
        });
        toast.success('Environment overrides saved');
        setDirty(false);
      } catch (error: unknown) {
        toast.error(`Failed to save: ${extractErrorMessage(error)}`);
      } finally {
        setSaving(false);
      }
    }, [drafts, envVarStore, processId, workspaceId]);

    // If no shared vars exist at all, render nothing
    if (sharedVars.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-balance font-semibold text-lg">
            Environment Overrides
          </h2>
          {availableVarsToAdd.length > 0 && (
            <Select onValueChange={handleAddOverride} value="">
              <SelectTrigger className="w-auto gap-1 px-3">
                <Plus className="mr-1 size-4" />
                <SelectValue placeholder="Add a variable" />
              </SelectTrigger>
              <SelectContent>
                {availableVarsToAdd.map(v => (
                  <SelectItem key={v.id} value={v.id}>
                    <span className="font-mono text-xs">{v.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {drafts.length === 0 ? (
          <div className="rounded-xl bg-card px-5 py-8 text-center shadow-verail">
            <p className="text-pretty text-muted-foreground text-sm">
              No environment variable overrides for this workspace.
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-card p-6 shadow-verail">
            <h3 className="mb-1 font-medium text-lg">Environment Overrides</h3>
            <p className="mb-4 text-muted-foreground text-sm">
              Override environment variable values for this workspace. These
              take priority over process-level values during generation.
            </p>
            <EnvVarTable
              headers={[
                { label: 'Name' },
                { label: 'Value' },
                { label: 'Enabled', center: true },
                { label: 'Actions', center: true }
              ]}
            >
              {drafts.map((draft, index) => (
                <tr key={draft.envVarId}>
                  <EnvVarCellText>{draft.name}</EnvVarCellText>
                  <EnvVarCellInput>
                    <EnvVarValueCell
                      onChange={value => updateDraft(index, { value })}
                      type={draft.type}
                      value={draft.value}
                    />
                  </EnvVarCellInput>
                  <EnvVarCellCenter>
                    <Switch
                      checked={draft.enabled}
                      onCheckedChange={v => updateDraft(index, { enabled: v })}
                    />
                  </EnvVarCellCenter>
                  <EnvVarCellCenter>
                    <Button
                      onClick={() => handleRemoveOverride(index)}
                      size="icon"
                      variant="ghost"
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </EnvVarCellCenter>
                </tr>
              ))}
            </EnvVarTable>
            <div className="mt-4 flex justify-end">
              <Button
                disabled={!dirty || saving}
                onClick={() => void handleSave()}
                size="sm"
              >
                {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);
